import type { ColumnDef, RowSelectionState } from '@tanstack/vue-table'
import VueDatepickerUi from 'vue-datepicker-ui'
import type { ModelRef } from 'vue'
import { Checkbox } from '~/components/ui/checkbox'
import TextInput from '~/components/TextInput.vue'
import DropDownInput from '~/components/DropDownInput.vue'
import MultiSelector from '~/components/MultiSelector.vue'
import ButtonWithIcon from '~/components/ButtonWithIcon.vue'
import FileDocument from '~/components/FileDocument.vue'

function findEntryByUniqueId(
  data: EsgDraftEntryData[],
  uniqueId: string,
): EsgDraftEntryData | undefined {
  return data.find(entry => entry.uniqueId === uniqueId)
}

export const createColumns = (
  filteredDraftData: ModelRef<EsgDraftEntryData[]>,
  rowSelection: Ref<RowSelectionState>,
  emit: GhgDataEntryTableEvents,
  hasSubmissionId: Ref<boolean>,
  hasPreviousSubmissionId: Ref<boolean>,
  units: CategoryUnit[],
  assets: AssetItem[],
  labels: Ref<CompanyLabel[]>,
  allSelected: Ref<boolean>,
): ColumnDef<EsgDraftEntryData>[] => [
  {
    id: 'date',
    accessorKey: 'date',
    header: () => h('div', { class: 'flex items-center' }, [
      !hasSubmissionId.value && h(Checkbox, {
        'v-model:checked': allSelected.value,
        'onUpdate:checked': (value: boolean) => emit('selectAll', !!value),
      }),
      h('span', { class: 'text-sm text-slate-800 font-semibold whitespace-nowrap ml-2.5' }, 'Date(DD/MM/YYYY)'),
      h('span', { class: 'text-red-400 ml-1' }, '*'),
    ]),
    cell: ({ row }) => h('div', { class: 'flex items-center gap-2.5' }, [
      !hasSubmissionId.value && h(Checkbox, {
        'checked': rowSelection.value[row.original.uniqueId] ?? false,
        'onUpdate:checked': (value: boolean) => {
          // Manually update datatable selection state
          const rowId = row.original.uniqueId
          if (value) {
            rowSelection.value = { ...rowSelection.value, [rowId]: true }
          }
          else {
            const { [rowId]: removed, ...rest } = rowSelection.value
            rowSelection.value = rest
          }
          // This alone would have handled the state update if it wasn't for the package's bug
          row.toggleSelected(!!value)
          // Sync selection with the table data
          const entry = findEntryByUniqueId(filteredDraftData.value, row.original.uniqueId)
          if (entry) entry.isChecked = !!value
          emit('rowSelectionChange', row.index, !!value)
        },
      }),
      h(VueDatepickerUi, {
        'id': `date-${row.original.uniqueId}`,
        'modelValue': findEntryByUniqueId(filteredDraftData.value, row.original.uniqueId)?.date,
        'onUpdate:modelValue': (value: string | Date) => {
          const entry = findEntryByUniqueId(filteredDraftData.value, row.original.uniqueId)
          if (entry) entry.date = value
          emit('rowUpdated', row.index)
        },
        'lang': 'en',
        'input-class': 'block w-full px-2 !h-12 m-1 leading-tight bg-white border-gray-300 rounded-sm shadow-sm placeholder-slate-300 border-1 text-primary focus:ring-main-primary focus:shadow-outline focus:border-main-primary placeholder:italic',
        'format': 'dd/MM/yyyy',
        'disabled': hasSubmissionId.value,
        'onClick': async () => {
          await nextTick()

          const inputElement = document.getElementById(`date-${row.original.uniqueId}`)
          if (!inputElement) return

          const rect = inputElement.getBoundingClientRect()
          const viewportHeight = window.innerHeight
          const datepickerContentElement = document.querySelector('.v-calendar.left .content') as HTMLElement

          if (datepickerContentElement) {
            // Temporarily show it to measure height (if hidden)
            datepickerContentElement.style.visibility = 'hidden'
            datepickerContentElement.style.display = 'block'
            const datepickerHeight = datepickerContentElement.offsetHeight
            datepickerContentElement.style.display = ''
            datepickerContentElement.style.visibility = ''

            // Decide whether to show above or below
            const spaceBelow = viewportHeight - rect.bottom
            const spaceAbove = rect.top

            const showAbove = spaceBelow < datepickerHeight && spaceAbove > datepickerHeight
            const top = showAbove
              ? rect.top + window.scrollY - datepickerHeight
              : rect.bottom + window.scrollY

            const left = rect.left

            datepickerContentElement.style.position = 'fixed'
            datepickerContentElement.style.top = `${top}px`
            datepickerContentElement.style.left = `${left}px`
          }
        }
        ,
      }),
    ]),
  },
  {
    id: 'item_name',
    accessorKey: 'item_name',
    header: () => h('div', { class: 'flex items-center gap-0.5' }, [
      h('span', { class: 'text-sm text-slate-800 font-semibold capitalize whitespace-nowrap' }, 'item description'),
      h('span', { class: 'text-red-400' }, '*'),
    ]),
    cell: ({ row }) => h(TextInput, {
      val: findEntryByUniqueId(filteredDraftData.value, row.original.uniqueId)?.item_name,
      onInput: (value: string) => {
        const entry = findEntryByUniqueId(filteredDraftData.value, row.original.uniqueId)
        if (entry) entry.item_name = value
        emit('rowUpdated', row.index)
      },
      classes: '!w-[200px] py-3.5',
      disabled: hasSubmissionId.value,
    }),
  },
  {
    id: 'value',
    accessorKey: 'value',
    header: () => h('div', { class: 'flex items-center gap-0.5' }, [
      h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'amount'),
      h('span', { class: 'text-red-400' }, '*'),
    ]),
    cell: ({ row }) => h(TextInput, {
      val: findEntryByUniqueId(filteredDraftData.value, row.original.uniqueId)?.value,
      onInput: (value: string) => {
        const entry = findEntryByUniqueId(filteredDraftData.value, row.original.uniqueId)
        if (entry) entry.value = value
        emit('rowUpdated', row.index)
      },
      classes: '!w-[120px] py-3.5',
      disabled: hasSubmissionId.value,
    }),
  },
  {
    id: 'unit_id',
    accessorKey: 'unit_id',
    header: () => h('div', { class: 'flex items-center gap-0.5' }, [
      h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'input unit'),
      h('span', { class: 'text-red-400' }, '*'),
    ]),
    cell: ({ row }) => h(DropDownInput, {
      key: row.original.uniqueId,
      displayKey: 'name',
      valueKey: 'id',
      options: units,
      disabled: hasSubmissionId.value,
      firstValue: row.original.unit_id!,
      maxWidthClass: 'max-w-[200px]',
      disableLocalization: true,
      onValueUpdated: (value: number) => {
        const entry = findEntryByUniqueId(filteredDraftData.value, row.original.uniqueId)
        if (entry) entry.unit_id = value
        emit('rowUpdated', row.index)
      },
    }),
  },
  {
    id: 'asset_id',
    accessorKey: 'asset_id',
    header: () => h('div', { class: 'flex items-center gap-0.5' }, [
      h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'asset'),
      h('span', { class: 'text-red-400' }, '*'),
    ]),
    cell: ({ row }) => h(DropDownInput, {
      key: row.original.uniqueId,
      displayKey: 'asset_name',
      valueKey: 'id',
      options: assets,
      disabled: hasSubmissionId.value,
      firstValue: row.original.asset_id!,
      maxWidthClass: 'max-w-[200px]',
      onValueUpdated: (value: number) => {
        const entry = findEntryByUniqueId(filteredDraftData.value, row.original.uniqueId)
        if (entry) entry.asset_id = value
        emit('rowUpdated', row.index)
      },
    }),
  },
  {
    id: 'labels',
    accessorKey: 'selectedLabels',
    header: () => h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'labels'),
    cell: ({ row }) => h(MultiSelector, {
      options: labels.value,
      optionsToEdit: row.original.selectedLabels!,
      displayProp: 'label_name',
      valueProp: 'id',
      customClass: '!w-[230px]',
      disabled: hasSubmissionId.value,
      position: 'items-left',
      onValuesUpdated: (value: string[]) => {
        const entry = findEntryByUniqueId(filteredDraftData.value, row.original.uniqueId)
        if (entry) entry.selectedLabels = value.map(label_id => labels.value.find(({ id }) => id === Number(label_id))!)
        emit('rowUpdated', row.index)
      },
    }),
  },
  {
    id: 'remark',
    accessorKey: 'remark',
    header: () => h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'remarks'),
    cell: ({ row }) => {
      let remarkLeft = 0
      let remarkTop = 0
      let remarkWidth = 0
      const entry = findEntryByUniqueId(filteredDraftData.value, row.original.uniqueId)
      if (!entry) return null
      const { remark, seeFullRemark } = entry

      return h('div', { class: 'relative' }, [
        h(TextInput, {
          val: entry?.remark || '-',
          classes: '!w-[200px] py-3.5',
          disabled: hasSubmissionId.value,
          onFocusin: () => entry.seeFullRemark = !entry.seeFullRemark,
          onFocusout: () => entry.seeFullRemark = false,
          onFocus: (event: Event) => {
            const targetElement = event.target as HTMLElement
            const rect = targetElement.getBoundingClientRect()
            const { innerWidth } = window

            let left = rect.left
            const top = rect.top + rect.height
            const width = rect.width

            if (left + width > innerWidth) {
              left = innerWidth - width - 10
            }

            remarkLeft = left
            remarkTop = top + rect.height
            remarkWidth = width
          },
          onInput: (value: string) => {
            const entry = findEntryByUniqueId(filteredDraftData.value, row.original.uniqueId)
            if (entry) entry.remark = value
            emit('rowUpdated', row.index)
          },
        }),
        seeFullRemark && !!remark && h('div', {
          class: 'h-min-content fixed left-0 top-full z-50 mt-2 w-full rounded-sm border border-[#A1B2C6] bg-[#F6F8FA] p-2.5 text-[#A1B2C6] transition duration-300 ease-in-out',
          style: {
            left: remarkLeft + 'px',
            top: remarkTop + 'px',
            width: remarkWidth + 'px',
          },
        }, [
          h('p', { class: 'break-words' }, entry.remark!),
          h(
            'button',
            { type: 'button', class: 'mt-1.5 text-[#0574B6] outline-none', onClick: () => entry.seeFullRemark = false },
            'See less',
          ),
        ]),
      ])
    },
  },
  {
    id: 'supporting_documents',
    accessorKey: 'supporting_documents',
    header: () => h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'attachments'),
    cell: ({ row }) => {
      const entry = findEntryByUniqueId(filteredDraftData.value, row.original.uniqueId)
      if (!entry) return null
      const { supporting_documents } = entry

      return h(
        'div',
        {
          class: [
            'px-2',
            (hasSubmissionId.value || hasPreviousSubmissionId.value) && 'cursor-not-allowed',
          ].filter(Boolean).join(' '),
        },
        supporting_documents && supporting_documents.length && h('div', { class: 'flex items-center space-x-1' }, h(ButtonWithIcon, {
          textColor: 'text-white',
          bgColors: 'bg-main-primary',
          onClick: () => emit('viewAttachment', supporting_documents, row.index),
        }, {
          default: () => h('span', { class: 'font-bold text-md' }, supporting_documents.length),
          icon: () => h(FileDocument),
        })),
      )
    },
  },
]
